import { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';

interface LineItem {
  id: string;
  description: string;
  details?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  category: string;
  isEdited?: boolean;
  originalAmount?: number;
  isCustom?: boolean;
}

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  billTo: {
    companyName: string;
    contactName: string;
    email: string;
    phone: string;
  };
  shipTo: {
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  orderNumber: string;
  poNumber: string;
  sidemark: string;
  sections: {
    screens: LineItem[];
    screenSubtotal: number;
    discountRate: number;
    discountAmount: number;
    afterScreenDiscount: number;
    accessories: LineItem[];
    accessoriesSubtotal: number;
    additionalCharges: LineItem[];
    additionalSubtotal: number;
    openRollDiscount?: number;
    promoDiscountAmount?: number;
    promoLabel?: string | null;
  };
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  totalAmount: number;
  depositPaid: number;
  achDiscountAmount?: number;
  refundedAmount?: number;
  ccProcessingFee?: number;
  balanceDue: number;
  notes: string;
  terms: string;
}

interface InvoiceTabProps {
  order: any;
  onRefresh: () => void;
}

const fmt = (n: number) => `$${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

export function InvoiceTab({ order, onRefresh }: InvoiceTabProps) {
  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadInvoice();
  }, [order.id]);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await api.orders.getInvoice(order.id);
      setInvoice(data);
      setDirty(false);
    } catch (err) {
      console.error('Failed to load invoice:', err);
      showToast('Failed to load invoice data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const recalcTotals = (inv: InvoiceData): InvoiceData => {
    const screenSubtotal = inv.sections.screens.reduce((s, l) => s + l.amount, 0);
    const discountAmount = screenSubtotal * (inv.sections.discountRate / 100);
    const afterScreenDiscount = screenSubtotal - discountAmount;
    const accessoriesSubtotal = inv.sections.accessories.reduce((s, l) => s + l.amount, 0);
    const additionalSubtotal = inv.sections.additionalCharges.reduce((s, l) => s + l.amount, 0);
    // Open Roll discount comes from the server (orders.open_roll_discount_amount).
    // We don't recompute it client-side since it depends on which items are
    // open-roll AND the live system_settings rate. Just carry it through.
    const openRollDiscount = inv.sections.openRollDiscount || 0;
    // Promo discount (per-dealer code OR system-wide sale) also comes from
    // the server. Carry through so the client-side total math reconciles.
    const promoDiscountAmount = inv.sections.promoDiscountAmount || 0;
    const subtotal = afterScreenDiscount + accessoriesSubtotal + additionalSubtotal - openRollDiscount - promoDiscountAmount;
    const taxAmount = subtotal * (inv.taxRate / 100);
    const totalAmount = subtotal + taxAmount;
    const achDiscountAmount = inv.achDiscountAmount || 0;
    const refundedAmount = inv.refundedAmount || 0;
    // Refunds returned to the dealer reduce their effective payment.
    const balanceDue = totalAmount - (inv.depositPaid - refundedAmount) - achDiscountAmount;

    return {
      ...inv,
      sections: {
        ...inv.sections,
        screenSubtotal,
        discountAmount,
        afterScreenDiscount,
        accessoriesSubtotal,
        additionalSubtotal,
        openRollDiscount,
        promoDiscountAmount,
      },
      subtotal,
      taxAmount,
      totalAmount,
      balanceDue,
    };
  };

  const startEdit = (cellId: string, currentValue: number) => {
    setEditingCell(cellId);
    setEditValue(currentValue.toFixed(2));
  };

  const commitEdit = (cellId: string) => {
    if (!invoice) return;
    const val = parseFloat(editValue);
    if (isNaN(val)) {
      setEditingCell(null);
      return;
    }

    const updated = { ...invoice, sections: { ...invoice.sections } };
    const [section, index, field] = cellId.split('-');
    const idx = parseInt(index);

    if (section === 'screen' && updated.sections.screens[idx]) {
      const item = { ...updated.sections.screens[idx] };
      if (field === 'unitPrice') {
        if (!item.isEdited) item.originalAmount = item.amount;
        item.unitPrice = val;
        item.amount = val * item.quantity;
        item.isEdited = true;
      }
      updated.sections.screens = [...updated.sections.screens];
      updated.sections.screens[idx] = item;
    } else if (section === 'accessory' && updated.sections.accessories[idx]) {
      const item = { ...updated.sections.accessories[idx] };
      if (field === 'unitPrice') {
        if (!item.isEdited) item.originalAmount = item.amount;
        item.unitPrice = val;
        item.amount = val * item.quantity;
        item.isEdited = true;
      } else if (field === 'quantity') {
        if (!item.isEdited) item.originalAmount = item.amount;
        item.quantity = Math.max(1, Math.floor(val));
        item.amount = item.unitPrice * item.quantity;
        item.isEdited = true;
      }
      updated.sections.accessories = [...updated.sections.accessories];
      updated.sections.accessories[idx] = item;
    } else if (section === 'additional' && updated.sections.additionalCharges[idx]) {
      const item = { ...updated.sections.additionalCharges[idx] };
      if (field === 'unitPrice') {
        if (!item.isEdited) item.originalAmount = item.amount;
        item.unitPrice = val;
        item.amount = val * item.quantity;
        item.isEdited = true;
      } else if (field === 'quantity') {
        if (!item.isEdited) item.originalAmount = item.amount;
        item.quantity = Math.max(1, Math.floor(val));
        item.amount = item.unitPrice * item.quantity;
        item.isEdited = true;
      }
      updated.sections.additionalCharges = [...updated.sections.additionalCharges];
      updated.sections.additionalCharges[idx] = item;
    } else if (section === 'discountRate') {
      updated.sections.discountRate = val;
    }

    setInvoice(recalcTotals(updated));
    setDirty(true);
    setEditingCell(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent, cellId: string) => {
    if (e.key === 'Enter') commitEdit(cellId);
    if (e.key === 'Escape') setEditingCell(null);
  };

  const addLineItem = (section: 'accessories' | 'additionalCharges') => {
    if (!invoice) return;
    const updated = { ...invoice, sections: { ...invoice.sections } };
    const newItem: LineItem = {
      id: `custom-${Date.now()}`,
      description: 'New Line Item',
      quantity: 1,
      unitPrice: 0,
      amount: 0,
      category: 'other',
      isCustom: true,
    };
    if (section === 'accessories') {
      updated.sections.accessories = [...updated.sections.accessories, newItem];
    } else {
      updated.sections.additionalCharges = [...updated.sections.additionalCharges, newItem];
    }
    setInvoice(recalcTotals(updated));
    setDirty(true);
  };

  const removeLineItem = (section: 'accessories' | 'additionalCharges', index: number) => {
    if (!invoice) return;
    const updated = { ...invoice, sections: { ...invoice.sections } };
    if (section === 'accessories') {
      updated.sections.accessories = updated.sections.accessories.filter((_, i) => i !== index);
    } else {
      updated.sections.additionalCharges = updated.sections.additionalCharges.filter((_, i) => i !== index);
    }
    setInvoice(recalcTotals(updated));
    setDirty(true);
  };

  const updateDescription = (section: 'screens' | 'accessories' | 'additionalCharges', index: number, desc: string) => {
    if (!invoice) return;
    const updated = { ...invoice, sections: { ...invoice.sections } };
    const arr = [...updated.sections[section]];
    arr[index] = { ...arr[index], description: desc };
    updated.sections[section] = arr;
    setInvoice(updated);
    setDirty(true);
  };

  const handleSave = async () => {
    if (!invoice) return;
    try {
      setSaving(true);
      await api.orders.saveInvoice(order.id, invoice);
      setDirty(false);
      showToast('Invoice saved — order total updated');
      onRefresh(); // Sync updated total to order header
    } catch (err) {
      console.error('Failed to save invoice:', err);
      showToast('Failed to save invoice', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!invoice) return;
    if (dirty) {
      await handleSave();
    }
    try {
      setSending(true);
      await api.orders.sendInvoice(order.id);
      showToast('Invoice sent to ' + (invoice.billTo.email || 'dealer'));
    } catch (err) {
      console.error('Failed to send invoice:', err);
      showToast('Failed to send invoice', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    if (!confirm('Reset invoice to current order data? This will discard all edits.')) return;
    loadInvoice();
  };

  const [writingOff, setWritingOff] = useState(false);
  const handleWriteOff = async () => {
    const reason = prompt(
      `Write off / comp order ${order.orderNumber || order.order_number}?\n\n` +
      `This ZEROES the order total, the invoice (no balance shown to the dealer), and settles AR as a $0 comp. Line items are kept for production history.\n\n` +
      `Enter a reason (who approved it / why):`,
    );
    if (reason === null) return; // cancelled
    try {
      setWritingOff(true);
      const res = await api.orders.writeOff(order.id, reason.trim());
      showToast(`Order written off (was ${fmt(res.writtenOffAmount || 0)})`);
      onRefresh();
      loadInvoice();
    } catch (err: any) {
      console.error('Write-off failed:', err);
      showToast(err.message || 'Failed to write off order', 'error');
    } finally {
      setWritingOff(false);
    }
  };

  const handlePrint = () => {
    if (!invoice) return;
    const w = window.open('', '_blank');
    if (!w) return;

    const screensRows = invoice.sections.screens.map(s =>
      `<tr>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;">${s.description}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;color:#6b7280;font-size:13px;">${s.details || ''}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${s.quantity}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(s.unitPrice)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(s.amount)}</td>
      </tr>`
    ).join('');

    const accessoryRows = invoice.sections.accessories.map(a =>
      `<tr>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;" colspan="2">
          ${a.description}${(a as any).details ? `<div style="font-size:11px;color:#6b7280;margin-top:2px;">${(a as any).details}</div>` : ''}
        </td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${a.quantity}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(a.unitPrice)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(a.amount)}</td>
      </tr>`
    ).join('');

    const additionalRows = invoice.sections.additionalCharges.map(c =>
      `<tr>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;" colspan="2">${c.description}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:center;">${c.quantity}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(c.unitPrice)}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #e5e7eb;text-align:right;">${fmt(c.amount)}</td>
      </tr>`
    ).join('');

    const shipToHtml = invoice.shipTo ? `
      <div>
        <p style="font-weight:600;margin-bottom:4px;color:#374151;">Ship To</p>
        <p style="color:#6b7280;margin:2px 0;">${invoice.shipTo.name || ''}</p>
        <p style="color:#6b7280;margin:2px 0;">${invoice.shipTo.street || ''}</p>
        <p style="color:#6b7280;margin:2px 0;">${invoice.shipTo.city || ''}${invoice.shipTo.state ? ', ' + invoice.shipTo.state : ''} ${invoice.shipTo.zip || ''}</p>
      </div>` : '';

    w.document.write(`<!DOCTYPE html><html><head>
      <title>Invoice ${invoice.invoiceNumber}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width:800px; margin:0 auto; padding:40px; color:#1f2937; font-size:14px; }
        table { width:100%; border-collapse:collapse; }
        @media print { body { padding:20px; } }
      </style>
    </head><body>
      <div style="display:flex;justify-content:space-between;align-items:start;border-bottom:3px solid #1f2937;padding-bottom:20px;margin-bottom:24px;">
        <div style="display:flex;align-items:start;gap:16px;">
          <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens" style="height:50px;width:auto;" />
          <div>
            <h1 style="font-size:28px;font-weight:800;letter-spacing:-0.5px;">INVOICE</h1>
            <p style="color:#6b7280;margin-top:4px;">${invoice.invoiceNumber}</p>
            ${invoice.orderNumber ? `<p style="color:#6b7280;">Order: ${invoice.orderNumber}</p>` : ''}
            ${invoice.poNumber ? `<p style="color:#6b7280;">PO: ${invoice.poNumber}</p>` : ''}
            ${invoice.sidemark ? `<p style="color:#6b7280;">Sidemark: ${invoice.sidemark}</p>` : ''}
          </div>
        </div>
        <div style="text-align:right;">
          <p style="font-weight:700;font-size:16px;">Keder Screens</p>
          <p style="color:#6b7280;margin-top:8px;">Date: ${invoice.invoiceDate}</p>
          <p style="color:#6b7280;">Due: ${invoice.dueDate}</p>
        </div>
      </div>

      <div style="display:flex;justify-content:space-between;margin-bottom:28px;">
        <div>
          <p style="font-weight:600;margin-bottom:4px;color:#374151;">Bill To</p>
          <p style="font-weight:500;">${invoice.billTo.companyName}</p>
          <p style="color:#6b7280;margin:2px 0;">${invoice.billTo.contactName}</p>
          <p style="color:#6b7280;margin:2px 0;">${invoice.billTo.email}</p>
          ${invoice.billTo.phone ? `<p style="color:#6b7280;margin:2px 0;">${invoice.billTo.phone}</p>` : ''}
        </div>
        ${shipToHtml}
      </div>

      <!-- Screens Section -->
      <table>
        <thead>
          <tr style="background:#eff6ff;">
            <th style="padding:10px;text-align:left;border-bottom:2px solid #bfdbfe;color:#1e40af;" colspan="5">Screens</th>
          </tr>
          <tr style="background:#f9fafb;">
            <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #e5e7eb;font-weight:600;">Description</th>
            <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #e5e7eb;font-weight:600;">Details</th>
            <th style="padding:8px 10px;text-align:center;border-bottom:1px solid #e5e7eb;font-weight:600;">Qty</th>
            <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:600;">Unit Price</th>
            <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:600;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${screensRows}
          <tr style="background:#f9fafb;">
            <td style="padding:8px 10px;font-weight:600;" colspan="4">Screen Subtotal</td>
            <td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.screenSubtotal)}</td>
          </tr>
          <tr>
            <td style="padding:8px 10px;color:#059669;" colspan="4">Dealer Discount (${invoice.sections.discountRate}%)</td>
            <td style="padding:8px 10px;text-align:right;color:#059669;">-${fmt(invoice.sections.discountAmount)}</td>
          </tr>
          <tr style="background:#f0fdf4;border-bottom:2px solid #bbf7d0;">
            <td style="padding:10px;font-weight:700;" colspan="4">Screens After Discount</td>
            <td style="padding:10px;text-align:right;font-weight:700;">${fmt(invoice.sections.afterScreenDiscount)}</td>
          </tr>
          ${(invoice.sections.openRollDiscount || 0) > 0 ? `
          <tr style="background:#f0fdf4;">
            <td style="padding:8px 10px;color:#15803d;font-weight:500;" colspan="4">Open Roll Discount (4% off dealer cost)</td>
            <td style="padding:8px 10px;text-align:right;color:#15803d;font-weight:500;">-${fmt(invoice.sections.openRollDiscount || 0)}</td>
          </tr>
          ` : ''}
          ${(invoice.sections.promoDiscountAmount || 0) > 0 ? `
          <tr style="background:#f0fdf4;">
            <td style="padding:8px 10px;color:#15803d;font-weight:500;" colspan="4">Promo${invoice.sections.promoLabel ? ` (${invoice.sections.promoLabel})` : ''}</td>
            <td style="padding:8px 10px;text-align:right;color:#15803d;font-weight:500;">-${fmt(invoice.sections.promoDiscountAmount || 0)}</td>
          </tr>
          ` : ''}
        </tbody>
      </table>

      ${invoice.sections.accessories.length > 0 ? `
      <table style="margin-top:20px;">
        <thead>
          <tr style="background:#fefce8;">
            <th style="padding:10px;text-align:left;border-bottom:2px solid #fde68a;color:#92400e;" colspan="5">Accessories & Add-ons</th>
          </tr>
          <tr style="background:#f9fafb;">
            <th style="padding:8px 10px;text-align:left;border-bottom:1px solid #e5e7eb;font-weight:600;" colspan="2">Description</th>
            <th style="padding:8px 10px;text-align:center;border-bottom:1px solid #e5e7eb;font-weight:600;">Qty</th>
            <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:600;">Unit Price</th>
            <th style="padding:8px 10px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:600;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${accessoryRows}
          <tr style="background:#f9fafb;">
            <td style="padding:8px 10px;font-weight:600;" colspan="4">Accessories Subtotal</td>
            <td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.accessoriesSubtotal)}</td>
          </tr>
        </tbody>
      </table>
      <p style="color:#9ca3af;font-size:12px;margin-top:4px;font-style:italic;">Accessories are not subject to dealer discount</p>
      ` : ''}

      ${invoice.sections.additionalCharges.length > 0 ? `
      <table style="margin-top:20px;">
        <thead>
          <tr style="background:#f3f4f6;">
            <th style="padding:10px;text-align:left;border-bottom:2px solid #d1d5db;color:#374151;" colspan="5">Additional Charges</th>
          </tr>
        </thead>
        <tbody>
          ${additionalRows}
          <tr style="background:#f9fafb;">
            <td style="padding:8px 10px;font-weight:600;" colspan="4">Additional Charges Subtotal</td>
            <td style="padding:8px 10px;text-align:right;font-weight:600;">${fmt(invoice.sections.additionalSubtotal)}</td>
          </tr>
        </tbody>
      </table>
      ` : ''}

      <!-- Totals -->
      <table style="margin-top:24px;width:50%;margin-left:auto;">
        <tbody>
          <tr>
            <td style="padding:6px 10px;font-weight:500;">Subtotal</td>
            <td style="padding:6px 10px;text-align:right;">${fmt(invoice.subtotal)}</td>
          </tr>
          ${invoice.taxAmount > 0 ? `<tr>
            <td style="padding:6px 10px;">Tax${invoice.subtotal > 0 ? ` <span style="color:#9ca3af;font-size:11px;">(${((invoice.taxAmount / invoice.subtotal) * 100).toFixed(2)}%)</span>` : ''}</td>
            <td style="padding:6px 10px;text-align:right;">${fmt(invoice.taxAmount)}</td>
          </tr>` : ''}
          <tr style="border-top:2px solid #1f2937;">
            <td style="padding:10px;font-weight:800;font-size:16px;">Total</td>
            <td style="padding:10px;text-align:right;font-weight:800;font-size:16px;">${fmt(invoice.totalAmount)}</td>
          </tr>
          ${invoice.depositPaid > 0 ? `<tr>
            <td style="padding:6px 10px;color:#6b7280;">Less: Payments Received</td>
            <td style="padding:6px 10px;text-align:right;color:#6b7280;">-${fmt(invoice.depositPaid)}</td>
          </tr>` : ''}
          ${(invoice.ccProcessingFee || 0) > 0 ? `<tr>
            <td style="padding:6px 10px;color:#6b7280;">Credit Card Processing Fee <span style="color:#9ca3af;font-size:11px;">(charged to card)</span></td>
            <td style="padding:6px 10px;text-align:right;color:#6b7280;">${fmt(invoice.ccProcessingFee || 0)}</td>
          </tr>` : ''}
          ${(invoice.achDiscountAmount || 0) > 0 ? `<tr>
            <td style="padding:6px 10px;color:#059669;">Less: ACH Payment Discount</td>
            <td style="padding:6px 10px;text-align:right;color:#059669;">-${fmt(invoice.achDiscountAmount || 0)}</td>
          </tr>` : ''}
          <tr style="background:#fef3c7;border:2px solid #f59e0b;">
            <td style="padding:12px;font-weight:800;font-size:18px;">Balance Due</td>
            <td style="padding:12px;text-align:right;font-weight:800;font-size:18px;">${fmt(invoice.balanceDue)}</td>
          </tr>
        </tbody>
      </table>

      ${invoice.notes ? `
      <div style="margin-top:28px;padding:16px;background:#f9fafb;border-radius:6px;">
        <p style="font-weight:600;margin-bottom:4px;">Notes</p>
        <p style="color:#6b7280;white-space:pre-wrap;">${invoice.notes}</p>
      </div>` : ''}

      <div style="margin-top:20px;padding:16px;background:#f9fafb;border-radius:6px;">
        <p style="font-weight:600;margin-bottom:4px;">Terms</p>
        <p style="color:#6b7280;">${invoice.terms || 'Net 30'}</p>
      </div>

    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  // Editable cell renderers — plain functions (NOT components) to avoid remount on re-render
  const renderAmount = (cellId: string, value: number) => {
    if (editingCell === cellId) {
      return (
        <input
          key={cellId}
          ref={editInputRef}
          type="number"
          step="0.01"
          className="w-24 text-right border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => commitEdit(cellId)}
          onKeyDown={(e) => handleEditKeyDown(e, cellId)}
        />
      );
    }
    return (
      <button
        key={cellId}
        onClick={() => startEdit(cellId, value)}
        className="text-right hover:bg-blue-50 rounded px-2 py-1 cursor-pointer transition-colors"
        title="Click to edit"
      >
        {fmt(value)}
      </button>
    );
  };

  // Quantity has the same edit behavior as amount but renders as a plain integer
  // (no $ sign, no decimals). Reusing renderAmount on a qty cell wrongly formats
  // the value as currency.
  const renderQuantity = (cellId: string, value: number) => {
    if (editingCell === cellId) {
      return (
        <input
          key={cellId}
          ref={editInputRef}
          type="number"
          step="1"
          min="1"
          className="w-16 text-center border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => commitEdit(cellId)}
          onKeyDown={(e) => handleEditKeyDown(e, cellId)}
        />
      );
    }
    return (
      <button
        key={cellId}
        onClick={() => startEdit(cellId, value)}
        className="text-center hover:bg-blue-50 rounded px-2 py-1 cursor-pointer transition-colors"
        title="Click to edit"
      >
        {Math.max(1, Math.floor(value))}
      </button>
    );
  };

  const renderDescription = (section: 'screens' | 'accessories' | 'additionalCharges', index: number, item: LineItem) => {
    const cellId = `desc-${section}-${index}`;
    if (editingCell === cellId) {
      return (
        <input
          key={cellId}
          ref={editInputRef}
          type="text"
          className="w-full border border-blue-400 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => { updateDescription(section, index, editValue); setEditingCell(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { updateDescription(section, index, editValue); setEditingCell(null); }
            if (e.key === 'Escape') setEditingCell(null);
          }}
        />
      );
    }
    return (
      <span
        key={cellId}
        className="cursor-pointer hover:text-blue-600"
        onClick={() => { setEditingCell(cellId); setEditValue(item.description); }}
      >
        {item.description}
        {item.isEdited && <span className="ml-1 inline-block w-2 h-2 bg-orange-400 rounded-full" title="Edited" />}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
        <p className="text-gray-500">Loading invoice...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-500">No invoice data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <h2 className="font-semibold text-lg">Invoice</h2>
          {dirty && (
            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          >
            Reset
          </button>
          {(order.written_off || order.writtenOff) ? (
            <span
              title={order.written_off_reason || order.writtenOffReason || 'Comped'}
              className="px-3 py-1.5 text-sm bg-purple-100 text-purple-800 border border-purple-300 rounded font-medium"
            >
              ✓ Written off / comped
            </span>
          ) : (
            <button
              onClick={handleWriteOff}
              disabled={writingOff}
              title="Comp this order — zeroes total, invoice, and AR balance"
              className="px-3 py-1.5 text-sm text-purple-700 border border-purple-300 rounded hover:bg-purple-50 disabled:opacity-50 transition-colors"
            >
              {writingOff ? 'Writing off…' : 'Write off / Comp'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-800 transition-colors"
          >
            Print / PDF
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="px-4 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {sending ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>

      {/* Correction Request Banner */}
      {(order.invoiceCorrectionRequestedAt || order.invoice_correction_requested_at) && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 flex items-start justify-between gap-4">
          <div>
            <p className="text-red-800 font-semibold text-sm flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              Dealer Requested Correction
            </p>
            <p className="text-red-700 text-sm mt-1 whitespace-pre-wrap">{order.invoiceCorrectionMessage || order.invoice_correction_message}</p>
            <p className="text-red-400 text-xs mt-1">
              {new Date(order.invoiceCorrectionRequestedAt || order.invoice_correction_requested_at).toLocaleString()}
            </p>
          </div>
          <button
            onClick={async () => {
              try {
                await api.clearInvoiceCorrection(order.id);
                onRefresh();
              } catch {}
            }}
            className="text-sm px-3 py-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 transition whitespace-nowrap"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Dealer Notes from Submit */}
      {(order.invoiceDealerNotes || order.invoice_dealer_notes || (invoice as any)?.dealerNotes) && (
        <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
          <p className="text-yellow-800 text-sm font-medium flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
            Dealer Note
          </p>
          <p className="text-yellow-700 text-sm mt-1">{order.invoiceDealerNotes || order.invoice_dealer_notes || (invoice as any)?.dealerNotes}</p>
        </div>
      )}

      {/* Payment Summary */}
      {((order.paymentAmount || order.payment_amount) > 0 || (order.achDiscountAmount || order.ach_discount_amount || 0) > 0) && (
        <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
          {(order.paymentAmount || order.payment_amount) > 0 && (
            <div className="flex items-center justify-between">
              <p className="text-green-800 text-sm font-medium">Payments Received</p>
              <p className="text-green-800 font-semibold">{fmt(order.paymentAmount || order.payment_amount || 0)}</p>
            </div>
          )}
          {(order.achDiscountAmount || order.ach_discount_amount || 0) > 0 && (
            <div className="flex items-center justify-between mt-1">
              <p className="text-green-700 text-sm font-medium">ACH Payment Discount</p>
              <p className="text-green-700 font-semibold">{fmt(order.achDiscountAmount || order.ach_discount_amount || 0)}</p>
            </div>
          )}
          {order.depositPaidAt || order.deposit_paid_at ? (
            <p className="text-green-600 text-xs mt-1">
              Deposit paid {new Date(order.depositPaidAt || order.deposit_paid_at).toLocaleDateString()}
              {(order.paidInFull || order.paid_in_full) && ' | Paid in full'}
            </p>
          ) : null}
          {invoice && (
            <p className="text-green-700 text-xs mt-1">
              Balance Due: <span className="font-semibold">{fmt(invoice.balanceDue)}</span>
              {invoice.balanceDue <= 0.5 && ' — Fully paid'}
            </p>
          )}
        </div>
      )}

      {/* Shipping Adjustment Status */}
      {(order.shippingAdjustmentStatus || order.shipping_adjustment_status) && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-blue-800 text-sm font-medium">
              Shipping Adjustment: {fmt(order.shippingAdjustmentAmount || order.shipping_adjustment_amount || 0)}
            </p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              (order.shippingAdjustmentStatus || order.shipping_adjustment_status) === 'pending'
                ? 'bg-amber-100 text-amber-700'
                : (order.shippingAdjustmentStatus || order.shipping_adjustment_status) === 'paid'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {(order.shippingAdjustmentStatus || order.shipping_adjustment_status)?.replace('_', ' ')}
            </span>
          </div>
          {(order.shippingAdjustmentDueDate || order.shipping_adjustment_due_date) && (
            <p className="text-blue-600 text-xs mt-1">
              Net 15 — Due by {new Date(order.shippingAdjustmentDueDate || order.shipping_adjustment_due_date).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Invoice Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start border-b-2 border-gray-800 pb-4 mb-4">
          <div className="flex items-start gap-4">
            <img src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png" alt="Keder Screens" className="h-12 w-auto" />
            <div>
            <h1 className="text-2xl font-extrabold tracking-tight">INVOICE</h1>
            <p className="text-gray-500 mt-1">{invoice.invoiceNumber}</p>
            {invoice.orderNumber && <p className="text-gray-500 text-sm">Order: {invoice.orderNumber}</p>}
            {invoice.poNumber && <p className="text-gray-500 text-sm">PO: {invoice.poNumber}</p>}
            {invoice.sidemark && <p className="text-gray-500 text-sm">Sidemark: {invoice.sidemark}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold text-lg">Keder Screens</p>
            <div className="mt-2 text-sm text-gray-500 space-y-1">
              <div className="flex items-center justify-end gap-2">
                <span>Date:</span>
                <input
                  type="date"
                  className="border rounded px-2 py-0.5 text-sm"
                  value={invoice.invoiceDate}
                  onChange={(e) => { setInvoice({ ...invoice, invoiceDate: e.target.value }); setDirty(true); }}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <span>Due:</span>
                <input
                  type="date"
                  className="border rounded px-2 py-0.5 text-sm"
                  value={invoice.dueDate}
                  onChange={(e) => { setInvoice({ ...invoice, dueDate: e.target.value }); setDirty(true); }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bill To / Ship To */}
        <div className="grid grid-cols-2 gap-8">
          <div>
            <p className="font-semibold text-gray-700 mb-1">Bill To</p>
            <p className="font-medium">{invoice.billTo.companyName}</p>
            <p className="text-gray-500 text-sm">{invoice.billTo.contactName}</p>
            <p className="text-gray-500 text-sm">{invoice.billTo.email}</p>
            {invoice.billTo.phone && <p className="text-gray-500 text-sm">{invoice.billTo.phone}</p>}
          </div>
          {invoice.shipTo && (
            <div>
              <p className="font-semibold text-gray-700 mb-1">Ship To</p>
              {invoice.shipTo.name && <p className="text-sm">{invoice.shipTo.name}</p>}
              {invoice.shipTo.street && <p className="text-gray-500 text-sm">{invoice.shipTo.street}</p>}
              <p className="text-gray-500 text-sm">
                {invoice.shipTo.city}{invoice.shipTo.state ? ', ' + invoice.shipTo.state : ''} {invoice.shipTo.zip}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Screens Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-blue-50 px-4 py-2.5 border-b-2 border-blue-200">
          <h3 className="font-semibold text-blue-800">Screens</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 text-sm">
              <th className="text-left px-4 py-2 font-semibold">Description</th>
              <th className="text-left px-4 py-2 font-semibold">Details</th>
              <th className="text-center px-4 py-2 font-semibold w-16">Qty</th>
              <th className="text-right px-4 py-2 font-semibold w-28">Unit Price</th>
              <th className="text-right px-4 py-2 font-semibold w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.sections.screens.map((screen, i) => (
              <tr key={screen.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2.5 text-sm">
                  {renderDescription('screens', i, screen)}
                </td>
                <td className="px-4 py-2.5 text-sm text-gray-500">{screen.details}</td>
                <td className="px-4 py-2.5 text-sm text-center">{screen.quantity}</td>
                <td className="px-4 py-2.5 text-sm text-right">
                  {renderAmount(`screen-${i}-unitPrice`, screen.unitPrice)}
                </td>
                <td className="px-4 py-2.5 text-sm text-right font-medium">{fmt(screen.amount)}</td>
              </tr>
            ))}
            {/* Screen Subtotal */}
            <tr className="bg-gray-50 border-b">
              <td className="px-4 py-2 font-semibold text-sm" colSpan={4}>Screen Subtotal</td>
              <td className="px-4 py-2 text-right font-semibold text-sm">{fmt(invoice.sections.screenSubtotal)}</td>
            </tr>
            {/* Discount */}
            <tr className="border-b">
              <td className="px-4 py-2 text-sm text-green-600" colSpan={3}>
                Dealer Discount
              </td>
              <td className="px-4 py-2 text-right text-green-600 text-sm">
                <span className="inline-flex items-center gap-1">
                  (
                  {renderAmount('discountRate-0-unitPrice', invoice.sections.discountRate)}
                  %)
                </span>
              </td>
              <td className="px-4 py-2 text-right text-green-600 text-sm font-medium">-{fmt(invoice.sections.discountAmount)}</td>
            </tr>
            {/* After Discount */}
            <tr className="bg-green-50 border-b-2 border-green-200">
              <td className="px-4 py-2.5 font-bold text-sm" colSpan={4}>Screens After Discount</td>
              <td className="px-4 py-2.5 text-right font-bold text-sm">{fmt(invoice.sections.afterScreenDiscount)}</td>
            </tr>
            {/* Open Roll Discount — 4% off dealer cost on open-roll units */}
            {(invoice.sections.openRollDiscount || 0) > 0 && (
              <tr className="bg-green-50">
                <td className="px-4 py-2 text-green-700 text-sm font-medium" colSpan={4}>
                  Open Roll Discount (4% off dealer cost)
                </td>
                <td className="px-4 py-2 text-right text-green-700 text-sm font-medium">
                  -{fmt(invoice.sections.openRollDiscount || 0)}
                </td>
              </tr>
            )}
            {/* Promo (per-dealer code or system-wide sale) */}
            {(invoice.sections.promoDiscountAmount || 0) > 0 && (
              <tr className="bg-green-50">
                <td className="px-4 py-2 text-green-700 text-sm font-medium" colSpan={4}>
                  Promo{invoice.sections.promoLabel ? ` (${invoice.sections.promoLabel})` : ''}
                </td>
                <td className="px-4 py-2 text-right text-green-700 text-sm font-medium">
                  -{fmt(invoice.sections.promoDiscountAmount || 0)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Accessories Section */}
      {(invoice.sections.accessories.length > 0 || true) && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-amber-50 px-4 py-2.5 border-b-2 border-amber-200 flex items-center justify-between">
            <h3 className="font-semibold text-amber-800">Accessories & Add-ons</h3>
            <button
              onClick={() => addLineItem('accessories')}
              className="text-xs text-amber-700 hover:text-amber-900 font-medium"
            >
              + Add Line
            </button>
          </div>
          {invoice.sections.accessories.length > 0 ? (
            <>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-sm">
                    <th className="text-left px-4 py-2 font-semibold">Description</th>
                    <th className="text-center px-4 py-2 font-semibold w-16">Qty</th>
                    <th className="text-right px-4 py-2 font-semibold w-28">Unit Price</th>
                    <th className="text-right px-4 py-2 font-semibold w-28">Amount</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.sections.accessories.map((acc, i) => (
                    <tr key={acc.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2.5 text-sm">
                        {renderDescription('accessories', i, acc)}
                        {(acc as any).details && (
                          <div className="text-xs text-gray-500 mt-0.5">{(acc as any).details}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-center">
                        {acc.isCustom
                          ? renderQuantity(`accessory-${i}-quantity`, acc.quantity)
                          : acc.quantity}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right">
                        {renderAmount(`accessory-${i}-unitPrice`, acc.unitPrice)}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium">{fmt(acc.amount)}</td>
                      <td className="px-4 py-2.5 text-center">
                        {acc.isCustom && (
                          <button
                            onClick={() => removeLineItem('accessories', i)}
                            className="text-red-400 hover:text-red-600 text-xs"
                            title="Remove"
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2 font-semibold text-sm" colSpan={3}>Accessories Subtotal</td>
                    <td className="px-4 py-2 text-right font-semibold text-sm">{fmt(invoice.sections.accessoriesSubtotal)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
              <p className="px-4 py-2 text-xs text-gray-400 italic">Accessories are not subject to dealer discount</p>
            </>
          ) : (
            <p className="px-4 py-4 text-sm text-gray-400">No accessories on this order</p>
          )}
        </div>
      )}

      {/* Additional Charges Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gray-100 px-4 py-2.5 border-b-2 border-gray-300 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">Additional Charges</h3>
          <button
            onClick={() => addLineItem('additionalCharges')}
            className="text-xs text-gray-600 hover:text-gray-800 font-medium"
          >
            + Add Line
          </button>
        </div>
        {invoice.sections.additionalCharges.length > 0 ? (
          <table className="w-full">
            <tbody>
              {invoice.sections.additionalCharges.map((charge, i) => (
                <tr key={charge.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm">
                    {renderDescription('additionalCharges', i, charge)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-center w-16">
                    {charge.isCustom
                      ? renderQuantity(`additional-${i}-quantity`, charge.quantity)
                      : charge.quantity}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-right w-28">
                    {renderAmount(`additional-${i}-unitPrice`, charge.unitPrice)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-right font-medium w-28">{fmt(charge.amount)}</td>
                  <td className="px-4 py-2.5 text-center w-10">
                    {charge.isCustom && (
                      <button
                        onClick={() => removeLineItem('additionalCharges', i)}
                        className="text-red-400 hover:text-red-600 text-xs"
                        title="Remove"
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 font-semibold text-sm" colSpan={3}>Additional Charges Subtotal</td>
                <td className="px-4 py-2 text-right font-semibold text-sm">{fmt(invoice.sections.additionalSubtotal)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-4 text-sm text-gray-400">No additional charges</p>
        )}
      </div>

      {/* Totals */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="ml-auto w-72 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium">Subtotal</span>
            <span>{fmt(invoice.subtotal)}</span>
          </div>
          {invoice.taxAmount > 0 && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>Tax {invoice.subtotal > 0 && (
                <span className="text-xs text-gray-400">({((invoice.taxAmount / invoice.subtotal) * 100).toFixed(2)}%)</span>
              )}</span>
              <span>{fmt(invoice.taxAmount)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t-2 border-gray-800">
            <span className="font-bold text-lg">Total</span>
            <span className="font-bold text-lg">{fmt(invoice.totalAmount)}</span>
          </div>
          {invoice.depositPaid > 0 && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>Less: Payments Received</span>
              <span>-{fmt(invoice.depositPaid)}</span>
            </div>
          )}
          {(invoice.ccProcessingFee || 0) > 0 && (
            <div className="flex justify-between text-sm text-gray-500">
              <span>Credit Card Processing Fee <span className="text-gray-400 text-xs">(charged to card)</span></span>
              <span>{fmt(invoice.ccProcessingFee || 0)}</span>
            </div>
          )}
          {(invoice.achDiscountAmount || 0) > 0 && (
            <div className="flex justify-between text-sm text-green-700">
              <span>Less: ACH Payment Discount</span>
              <span>-{fmt(invoice.achDiscountAmount || 0)}</span>
            </div>
          )}
          {(invoice.refundedAmount || 0) > 0 && (
            <div className="flex justify-between text-sm text-blue-600">
              <span>Add Back: Refunded to Dealer</span>
              <span>+{fmt(invoice.refundedAmount || 0)}</span>
            </div>
          )}
          <div className="flex justify-between p-3 bg-amber-50 border-2 border-amber-400 rounded-lg">
            <span className="font-extrabold text-lg">Balance Due</span>
            <span className="font-extrabold text-lg">{fmt(invoice.balanceDue)}</span>
          </div>
        </div>
      </div>

      {/* Notes & Terms */}
      <div className="bg-white rounded-lg shadow p-6 grid grid-cols-2 gap-6">
        <div>
          <label className="block font-semibold text-sm text-gray-700 mb-1">Notes</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Add notes to this invoice..."
            value={invoice.notes}
            onChange={(e) => { setInvoice({ ...invoice, notes: e.target.value }); setDirty(true); }}
          />
        </div>
        <div>
          <label className="block font-semibold text-sm text-gray-700 mb-1">Payment Terms</label>
          <textarea
            className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={3}
            value={invoice.terms}
            onChange={(e) => { setInvoice({ ...invoice, terms: e.target.value }); setDirty(true); }}
          />
        </div>
      </div>
    </div>
  );
}
